import { useState, useEffect, useMemo } from "react";
import { fetchEquipmentPolicies, fetchPendingPolicyChanges } from "../../../api/equipmentApi";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuthUser } from "../../../contexts/AuthUserContext";
import EditPolicy from "../../../components/policies/EditPolicy";
import NewPolicy from "../../../components/policies/NewPolicy";

// Helper function to format changedBy for display
const formatChangedBy = (changedBy, currentUser) => {
    if (!changedBy) return 'Unknown';
    
    // If it's an auth0 ID, try to match with current user
    if (changedBy.startsWith('auth0|')) {
        // Check if it matches current user's ID
        if (currentUser?._raw?.sub === changedBy) {
            // Use current user's name or email
            return currentUser.name || currentUser.email || changedBy;
        }
        // Otherwise, just return a cleaner format
        return changedBy.replace('auth0|', 'User ');
    }
    
    // If it's already an email or name, return as-is
    return changedBy;
};

// Helper function to format ISO timestamp
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
        const date = new Date(timestamp);
        const localMonth = String(date.getMonth() + 1).padStart(2, '0');
        const localDay = String(date.getDate()).padStart(2, '0');
        const localYear = String(date.getFullYear()).slice(2);
        const localHour = String(date.getHours()).padStart(2, '0');
        const localMinute = String(date.getMinutes()).padStart(2, '0');
        return `${localMonth}/${localDay}/${localYear} - ${localHour}:${localMinute}`;
    } catch (error) {
        return 'Invalid Date';
    }
};

// Helper function to highlight search text
const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
        regex.test(part) ? (
            <span key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
                {part}
            </span>
        ) : part
    );
};

const EquipmentPoliciesTable = ({ policies, searchText, onEdit }) => {
    return (
        <div className="w-full h-full rounded-md overflow-hidden flex flex-col">
            {/* Desktop Header (sm and above) */}
            <div className="w-full flex-shrink-0 hidden sm:block">
                <div className="flex text-font-primary py-3 px-0 card-container items-center text-sm font-semibold">
                    <div className="pl-4 w-[200px] max-w-[200px]">Policy</div>
                    <div className="pl-2 flex-1">Description</div>
                    <div className="pl-2 w-[60px] max-w-[60px] flex justify-center">Edit</div>
                </div>
            </div>

            <div className="w-full flex-1 overflow-y-auto scrollable">
                {/* Desktop List View (sm and above) */}
                <div className="w-full hidden sm:block">
                    {policies.map((policy, index) => {
                        const policyName = policy.policy_type || policy.name || 'Unnamed Policy';
                        const description = policy.policy_text || policy.policy_content || policy.content || 'No description available';
                        const updatedAt = policy.updated_at || policy.updatedAt;
                        const policyId = policy.id || policy.policy_id || policy.policyId;
                        
                        return (
                            <div
                                key={index}
                                className="flex mb-2 text-font-secondary hover:text-font-primary hover:bg-background hover:button-container hover:border-none transition-colors ease-out-in duration-300 text-sm"
                            >
                                <div className="flex flex-col gap-1 pt-2 pl-4 w-[200px] max-w-[200px] text-font-primary flex-shrink-0">
                                    <span className="font-semibold text-primary text-sm capitalize">{highlightText(policyName, searchText)}</span>
                                    <span className="text-sm text-font-secondary">{highlightText(formatTimestamp(updatedAt), searchText)}</span>
                                    {policy._hasPendingChanges && (
                                        <span className="text-xs text-orange-600 mt-1">Pending approval • Modified by {policy._changedBy}</span>
                                    )}
                                    {policy._isPendingCreation && (
                                        <span className="text-xs text-blue-600 mt-1">Pending approval • Created by {policy._changedBy}</span>
                                    )}
                                </div>
                                <div className="p-2 pr-4 flex-1 min-w-0">
                                    <div className="text-sm leading-relaxed">
                                        {highlightText(description, searchText)}
                                    </div>
                                </div>
                                <div className="flex items-center justify-center w-[60px] max-w-[60px] flex-shrink-0">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(policyId);
                                        }}
                                        className="p-2 text-primary hover:bg-accent hover:text-background rounded transition-colors duration-300"
                                        title="Edit Policy"
                                    >
                                        <Icon icon="mdi:pencil" className="text-xl" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Mobile Card View (below sm) */}
                <div className="w-full sm:hidden p-2">
                    {policies.map((policy, index) => {
                        const policyName = policy.policy_type || policy.name || 'Unnamed Policy';
                        const description = policy.policy_text || policy.policy_content || policy.content || 'No description available';
                        const updatedAt = policy.updated_at || policy.updatedAt;
                        const policyId = policy.id || policy.policy_id || policy.policyId;
                        
                        return (
                            <div
                                key={index}
                                className="p-3 rounded-md transition-all duration-300 ease-out-in hover:shadow-button group hover:scale-[1.01] card-container bg-background border border-bgAccent min-h-[60px] flex flex-col mb-2"
                            >
                                <div className="flex items-start justify-between mb-2 flex-shrink-0">
                                    <div className="flex items-center gap-2">
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-primary text-base capitalize">
                                                {highlightText(policyName, searchText)}
                                            </span>
                                            <span className="text-xs text-font-secondary">
                                                {highlightText(formatTimestamp(updatedAt), searchText)}
                                            </span>
                                            {policy._hasPendingChanges && (
                                                <span className="text-xs text-orange-600 mt-1">Pending approval • Modified by {policy._changedBy}</span>
                                            )}
                                            {policy._isPendingCreation && (
                                                <span className="text-xs text-blue-600 mt-1">Pending approval • Created by {policy._changedBy}</span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(policyId);
                                        }}
                                        className="p-2 text-primary hover:bg-accent hover:text-background rounded transition-colors duration-300"
                                        title="Edit Policy"
                                    >
                                        <Icon icon="mdi:pencil" className="text-xl" />
                                    </button>
                                </div>

                                <div className="mt-2">
                                    <p className="text-sm text-font-secondary leading-relaxed group-hover:text-font-primary">
                                        {highlightText(description, searchText)}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {policies.length === 0 && (
                    <div className="text-center py-10 text-gray-400">No policies found.</div>
                )}
            </div>
        </div>
    );
};

const EquipmentPolicies = () => {
    const [policies, setPolicies] = useState([]);
    const [pendingChanges, setPendingChanges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isNewPolicyMode, setIsNewPolicyMode] = useState(false);
    const [selectedPolicyId, setSelectedPolicyId] = useState(null);
    const currentUser = useAuthUser();

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        policiesTable: false
    });

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, policiesTable: true }));
        }, 300));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    const loadPolicies = async () => {
        try {
            setLoading(true);
            const response = await fetchEquipmentPolicies('wards');
            
            // Handle service-level errors
            if (response?.data?.error) {
                setError(response.data.message || 'Failed to load policies');
                setPolicies([]);
                return;
            }
            
            // Extract policies from response
            const policiesData = response?.data?.policies || [];
            setPolicies(policiesData);
            setError(null);
        } catch (err) {
            setError('Failed to load policies');
            console.error('Error loading policies:', err);
            setPolicies([]);
        } finally {
            setLoading(false);
        }
    };

    const loadPendingChanges = async () => {
        try {
            const response = await fetchPendingPolicyChanges('wards');
            
            // Handle the response structure
            let allChanges = [];
            if (response) {
                if (response.data && response.data.all && Array.isArray(response.data.all)) {
                    allChanges = response.data.all;
                } else if (Array.isArray(response)) {
                    allChanges = response;
                } else if (response.data && Array.isArray(response.data)) {
                    allChanges = response.data;
                }
            }
            
            // Filter for pending changes only
            const pending = allChanges.filter(change => change.changeStatus === 'pending');
            setPendingChanges(pending);
        } catch (err) {
            console.error('Error loading pending policy changes:', err);
            setPendingChanges([]);
        }
    };

    useEffect(() => {
        loadPolicies();
        loadPendingChanges();
    }, []);

    // Merge pending changes with policies
    const mergedPolicies = useMemo(() => {
        if (!policies || policies.length === 0) {
            return policies;
        }

        // Create a copy of policies
        const merged = policies.map(policy => ({ ...policy }));
        
        // Process pending changes
        if (pendingChanges && pendingChanges.length > 0) {
            // Separate modifications and creations
            const pendingModifications = pendingChanges.filter(change => 
                change.changeType === 'policy' && 
                change.changeAction !== 'create' && 
                change.changeStatus === 'pending'
            );
            const pendingCreations = pendingChanges.filter(change => 
                change.changeType === 'policy' && 
                change.changeAction === 'create' && 
                change.changeStatus === 'pending'
            );

            // Apply modifications to existing policies
            pendingModifications.forEach(change => {
                const policyId = String(change.policyId);
                const policyIndex = merged.findIndex(p => {
                    const pId = String(p.id || p.policy_id || p.policyId);
                    return pId === policyId;
                });
                
                if (policyIndex !== -1) {
                    // Apply modified data
                    if (change.modifiedData) {
                        merged[policyIndex] = {
                            ...merged[policyIndex],
                            ...change.modifiedData,
                            _hasPendingChanges: true,
                            _pendingChangeId: change.id,
                            _changedBy: formatChangedBy(change.changedBy || change.changed_by, currentUser),
                            _changedAt: change.changedAt || change.changed_at
                        };
                    }
                }
            });

            // Add pending creations as virtual policies
            pendingCreations.forEach(creation => {
                if (creation.newData) {
                    const virtualPolicy = {
                        id: `PENDING_${creation.id}`,
                        policy_type: creation.newData.policy_type || '',
                        policy_text: creation.newData.policy_text || '',
                        updated_at: creation.changedAt,
                        _isPendingCreation: true,
                        _pendingChangeId: creation.id,
                        _changedBy: formatChangedBy(creation.changedBy || creation.changed_by, currentUser),
                        _changedAt: creation.changedAt || creation.changed_at
                    };
                    merged.push(virtualPolicy);
                }
            });
        }

        return merged;
    }, [policies, pendingChanges]);

    const handleEditPolicy = (policyId) => {
        setSelectedPolicyId(policyId);
        setIsEditMode(true);
    };

    const handleAddNewPolicy = () => {
        setIsNewPolicyMode(true);
    };

    const handleBackToPolicies = () => {
        setIsEditMode(false);
        setIsNewPolicyMode(false);
        setSelectedPolicyId(null);
        loadPolicies(); // Refresh policies after edit/new
        loadPendingChanges(); // Refresh pending changes
    };

    // Filter policies based on search text
    const filteredPolicies = mergedPolicies.filter(policy => {
        if (searchText === '') return true;
        
        const searchLower = searchText.toLowerCase();
        const policyName = (policy.policy_type || policy.name || '').toLowerCase();
        const description = (policy.policy_text || policy.policy_content || policy.content || '').toLowerCase();
        const updatedAt = formatTimestamp(policy.updated_at || policy.updatedAt).toLowerCase();
        
        return policyName.includes(searchLower) || 
               description.includes(searchLower) || 
               updatedAt.includes(searchLower);
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-font-secondary">Loading policies...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-error">{error}</div>
            </div>
        );
    }

    if (isEditMode) {
        return (
            <EditPolicy 
                onBack={handleBackToPolicies}
                initialPolicyId={selectedPolicyId}
                policies={policies}
            />
        );
    }

    if (isNewPolicyMode) {
        return (
            <NewPolicy 
                onBack={handleBackToPolicies}
            />
        );
    }

    return (
        <div className="responsive-page-layout">
            <div className={`flex flex-col lg:flex-row justify-between lg:items-center items-start w-full gap-4 lg:gap-0 transition-all duration-700 ease-out ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                <div className="flex flex-col gap-2 items-start">
                    <h1 className="header-textbox">
                        <Icon icon="iconoir:privacy-policy" className="header-icon"></Icon>
                        Equipment Policies
                    </h1>
                    <p className="text-font-secondary text-sm sm:text-base">
                        View and manage equipment policies ({filteredPolicies.length} of {mergedPolicies.length} policies)
                    </p>
                </div>
                <div className="flex flex-row gap-2 w-full lg:w-auto">
                    <div className="relative w-full sm:w-auto">
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Search policies..."
                            className="p-2 sm:p-3 button-container rounded-md text-xs sm:text-sm w-full sm:w-auto pl-3"
                        />
                    </div>
                    <button
                        onClick={handleAddNewPolicy}
                        className="flex flex-row gap-2 items-center text-font-primary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="proicons:box-add" className="text-xl text-primary group-hover:text-background" />
                        <span className="text-sm font-medium group-hover:text-background">Add New Policy</span>
                    </button>
                </div>
            </div>
            
            <div className={`w-full flex-1 min-h-0 transition-all duration-700 ease-out ${
                animationStates.policiesTable
                    ? 'opacity-100 translate-y-0 scale-100'
                    : 'opacity-0 translate-y-4 scale-95'
            }`}>
                <EquipmentPoliciesTable 
                    policies={filteredPolicies}
                    searchText={searchText}
                    onEdit={handleEditPolicy}
                />
            </div>
        </div>
    );
};

export default EquipmentPolicies;

